import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UserDeviceService } from '../services/user-device.service';
import { targetsOurApi } from './api-target';

/**
 * Attaches this browser's device headers to every request that goes to our own API.
 *
 * Two constraints:
 *
 * 1. **It filters on `environment.apiUrl`** through `targetsOurApi`. Without that,
 *    `X-User-Device-Id` — a persistent identifier — would leak to Firebase, Google, every
 *    `./assets/i18n/*.json` fetch, and to any host whose name merely begins with ours.
 * 2. **It uses `peek()`, never `hash()`.** `peek()` is synchronous and cannot trigger a library load.
 *
 * An unavailable header is omitted rather than sent empty, so the server's "absent" and "present but
 * blank" cases stay distinguishable.
 */
@Injectable()
export class UserDeviceInterceptor implements HttpInterceptor {
  private userDevice = inject(UserDeviceService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!targetsOurApi(request.url)) {
      return next.handle(request);
    }
    const identity = this.userDevice.peek();
    const headers: { [key: string]: string } = {};
    if (identity.deviceId) {
      headers['X-User-Device-Id'] = identity.deviceId;
    }
    if (identity.hash) {
      headers['X-User-Device-Hash'] = identity.hash;
    }
    if (identity.incognito !== null) {
      // Sent as an explicit 'false' too, not omitted: the server reads an absent header as "unknown",
      // and "we checked, this is not a private session" is the more useful of the two answers.
      headers['X-User-Device-Incognito'] = identity.incognito ? 'true' : 'false';
    }
    if (!Object.keys(headers).length) {
      return next.handle(request);
    }
    return next.handle(request.clone({ setHeaders: headers }));
  }
}
